import React from 'react';
import { Link } from 'react-router-dom';
import { ShieldCheck, PhoneCall, ExternalLink, Lock, CheckCircle2, Heart } from 'lucide-react';

export const Footer: React.FC = () => {
  return (
    <footer className="bg-slate-950 border-t border-slate-800 text-slate-400 text-sm">
      {/* Australian Emergency Helplines Strip */}
      <div className="bg-slate-900/90 border-b border-slate-800/80 py-6 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div>
              <div className="flex items-center gap-2 text-white font-semibold text-base">
                <PhoneCall className="w-5 h-5 text-emerald-400" />
                <span>Official Australian Identity Emergency Contacts</span>
              </div>
              <p className="text-xs text-slate-400 mt-1">
                If you have suffered an active compromise or financial theft, these authorized services provide immediate assistance:
              </p>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 w-full md:w-auto text-xs">
              <a
                href="tel:1800595160"
                className="p-2.5 rounded-lg bg-slate-950 border border-slate-800 hover:border-emerald-700/60 transition-colors"
              >
                <div className="font-bold text-white flex items-center justify-between">
                  <span>IDCARE</span>
                  <span className="text-emerald-400 text-[10px]">FREE</span>
                </div>
                <div className="text-emerald-400 font-mono font-bold mt-0.5">1800 595 160</div>
                <div className="text-[10px] text-slate-400">National ID Support</div>
              </a>

              <a
                href="https://www.cyber.gov.au"
                target="_blank"
                rel="noreferrer"
                className="p-2.5 rounded-lg bg-slate-950 border border-slate-800 hover:border-blue-700/60 transition-colors"
              >
                <div className="font-bold text-white flex items-center justify-between">
                  <span>ReportCyber</span>
                  <ExternalLink className="w-3 h-3 text-slate-400" />
                </div>
                <div className="text-blue-400 font-mono font-bold mt-0.5">cyber.gov.au</div>
                <div className="text-[10px] text-slate-400">Police Incident Reports</div>
              </a>

              <a
                href="tel:1800941126"
                className="p-2.5 rounded-lg bg-slate-950 border border-slate-800 hover:border-indigo-700/60 transition-colors"
              >
                <div className="font-bold text-white flex items-center justify-between">
                  <span>Services Australia</span>
                  <span className="text-indigo-400 text-[10px]">myGov</span>
                </div>
                <div className="text-indigo-400 font-mono font-bold mt-0.5">1800 941 126</div>
                <div className="text-[10px] text-slate-400">Medicare & Centrelink</div>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Main Links */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-8">
          {/* Brand Col */}
          <div className="md:col-span-2 space-y-4">
            <Link to="/" className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-600 flex items-center justify-center">
                <ShieldCheck className="w-5 h-5 text-white" />
              </div>
              <div className="flex items-center gap-1">
                <span className="text-lg font-bold text-white">IDFraud</span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-emerald-900 text-emerald-300 font-mono font-semibold">.au</span>
              </div>
            </Link>
            <p className="text-sm text-slate-400 max-w-sm leading-relaxed">
              Australia's personal identity security centre. We help you detect exposure, understand risks, protect your identity documents, and recover step-by-step from fraud.
            </p>
            <div className="flex flex-col gap-2 pt-2 text-xs text-slate-400">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Zero Password Collection Policy</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Australian Privacy Principles (APP) Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                <span>Official Government Guidance Procedures</span>
              </div>
            </div>
          </div>

          {/* Tools */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Detection Tools</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/check-identity" className="hover:text-emerald-400 transition-colors">Identity Exposure Check</Link>
              </li>
              <li>
                <Link to="/scam-checker" className="hover:text-emerald-400 transition-colors">AI Scam Message Checker</Link>
              </li>
              <li>
                <Link to="/website-checker" className="hover:text-emerald-400 transition-colors">Website Safety Analyzer</Link>
              </li>
              <li>
                <Link to="/security-score" className="hover:text-emerald-400 transition-colors">Identity Security Score (0-100)</Link>
              </li>
              <li>
                <Link to="/id-watermark" className="hover:text-emerald-400 transition-colors">ID Watermark Tool</Link>
              </li>
            </ul>
          </div>

          {/* Protection & Recovery */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Protection & Recovery</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/identity-theft" className="text-rose-400 hover:text-rose-300 font-medium transition-colors">Emergency Centre</Link>
              </li>
              <li>
                <Link to="/identity-documents" className="hover:text-emerald-400 transition-colors">Driver Licence Guide</Link>
              </li>
              <li>
                <Link to="/identity-documents" className="hover:text-emerald-400 transition-colors">Medicare & TFN Protection</Link>
              </li>
              <li>
                <Link to="/account-security" className="hover:text-emerald-400 transition-colors">Account Security Audit</Link>
              </li>
              <li>
                <Link to="/family" className="hover:text-emerald-400 transition-colors">Family Protection</Link>
              </li>
            </ul>
          </div>

          {/* Resources & Legal */}
          <div className="space-y-3">
            <h4 className="text-xs font-bold text-slate-200 uppercase tracking-wider">Platform</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/how-it-works" className="hover:text-emerald-400 transition-colors">How It Works</Link>
              </li>
              <li>
                <Link to="/pricing" className="hover:text-emerald-400 transition-colors">Pricing & Plans</Link>
              </li>
              <li>
                <Link to="/resources" className="hover:text-emerald-400 transition-colors">Knowledge Base</Link>
              </li>
              <li>
                <Link to="/privacy" className="hover:text-emerald-400 transition-colors">Privacy Policy</Link>
              </li>
              <li>
                <Link to="/admin" className="text-slate-500 hover:text-slate-300 transition-colors">Admin Portal</Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Disclaimer & Copyright */}
        <div className="border-t border-slate-800/80 mt-10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-400">
          <p>
            © {new Date().getFullYear()} IDFraud.au. Built specifically for Australian consumers and businesses.
          </p>
          <p className="text-slate-400 max-w-xl text-center sm:text-right">
            Important Notice: Automated breach and scam checks provide risk assessment indicators and are not a legal guarantee. For emergency assistance with stolen identity, contact IDCARE on 1800 595 160.
          </p>
        </div>
      </div>
    </footer>
  );
};
